import { useCallback } from 'react';
import { Document, Task } from '../types';
import { users } from '../data/sampleData';

export const useDocumentManagement = (
  documents: Document[],
  tasks: Task[],
  onDocumentsUpdate: (documents: Document[]) => void,
  onTasksUpdate: (tasks: Task[]) => void,
  onTimelineEvent: (event: any) => void
) => {
  const handleDocumentUpload = useCallback((
    document: Omit<Document, 'id' | 'uploadDate'>,
    taskId?: string
  ) => {
    const newDocument: Document = {
      ...document,
      id: `d${documents.length + 1}`,
      uploadDate: new Date().toISOString(),
    };

    // Add document upload event to timeline
    const user = users.find(u => u.id === document.uploadedBy);
    const userName = user ? user.name : 'Unknown user';

    onTimelineEvent({
      date: new Date().toISOString(),
      description: `Document uploaded: "${document.title}"`,
      type: 'document-filed',
      addedBy: document.uploadedBy,
      documentId: newDocument.id,
    });

    // If a task was completed with this upload, update the task
    if (taskId) {
      const taskIndex = tasks.findIndex(t => t.id === taskId);
      if (taskIndex !== -1) {
        const updatedTasks = [...tasks];
        updatedTasks[taskIndex] = {
          ...updatedTasks[taskIndex],
          status: 'completed',
          metadata: {
            completedAt: new Date().toISOString(),
            completedWith: newDocument.id,
          },
        };

        // Add task completion event to timeline
        onTimelineEvent({
          date: new Date().toISOString(),
          description: `Task "${updatedTasks[taskIndex].title}" completed with document upload by ${userName}`,
          type: 'note',
          addedBy: document.uploadedBy,
          taskId: taskId,
          documentId: newDocument.id,
        });

        onTasksUpdate(updatedTasks);
      }
    }

    onDocumentsUpdate([...documents, newDocument]);
  }, [documents, tasks, onDocumentsUpdate, onTasksUpdate, onTimelineEvent]);

  return {
    handleDocumentUpload,
  };
};